import { useState, useEffect, useCallback, useMemo } from 'react';
import { cloneDeep, filter, groupBy, map } from 'lodash';
import {
  setChecklistLogItemStatus,
  createChecklistLogItemNote,
  createChecklistLogNote,
  updateChecklistLogItemNote,
  deleteChecklistItemNoteApi,
  updateChecklistLogNoteApi,
  deleteChecklistLogNoteApi,
} from 'api/checklistLogApi';
import { validateItemAutomations } from 'xeniautils';
import { AUTOMATION_ACTIONS } from 'components/AllDialogs/automation/utils/constants';
import xeniaApi from 'api/index';
import { useWorkspaceHook } from 'utils/CustomHooks/useWorkspaceHook';
import { useSelector } from 'react-redux';
import TaskChecklist from 'components/TaskChecklist';

const useChecklistLog = ({
  logId = undefined,
  taskId = undefined,
  checklistId,
  onChecklistLoadSuccess,
}: {
  logId?: string;
  taskId?: string;
  checklistId: string;
  onChecklistLoadSuccess?: () => void;
}) => {
  const { workspaceId } = useWorkspaceHook();
  const currentUser = useSelector((state: any) => state?.authData?.user);

  const [localChecklistLog, setLocalChecklistLog] = <any>useState(null);
  const [checklistLog, setChecklistLog] = <any>useState(null);

  const [automations, setAutomations] = <any>useState(null);
  const [childItemsMap, setChildItemsMap] = <any>useState(null);
  const [itemErrors, setItemErrors] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [currentItemId, setCurrentItemId] = useState<any>(null);
  const [checklistProgAndScore, setChecklistProgAndScore] = useState<any>({});

  const finalLogId = useMemo(() => {
    return logId ? logId : checklistLog ? checklistLog.id : null;
  }, [logId, checklistLog]);

  // Only set item errors initially
  useEffect(() => {
    if (checklistLog && Object.keys(itemErrors).length === 0) {
      setItemErrors(getInitialErrors(checklistLog));
    }
  }, [checklistLog]);

  // fetch checklistLog automations on checklistId change
  useEffect(() => {
    if (checklistId) {
      getChecklistAutomations(checklistId);
    }
  }, [checklistId]);

  // fetch checklistLog log if logId is present or start new
  useEffect(() => {
    if (finalLogId) {
      handleContinue(finalLogId);
    } else if (checklistId) {
      handleStart(checklistId);
    }
  }, [finalLogId, checklistId]);

  // checks error for the current item being changed
  useEffect(() => {
    if (currentItemId && localChecklistLog) {
      const item = localChecklistLog?.TaskChecklistItems?.find(
        (item) => item.id === currentItemId,
      );
      setSingleItemError(item);
    }
  }, [currentItemId, localChecklistLog]);

  const extractChecklistProgAndScore = (log) => {
    const sections = log.TaskChecklistItems.filter(
      (item) => item.type === 'header',
    );
    const newCount = {
      progress: log.progress,
      score: log.score,
      sectionScores: sections.reduce(
        (prev, curr) => ({
          ...prev,
          [curr.id]: {
            progress: curr.progress,
            score: curr.score,
          },
        }),
        {},
      ),
    };
    return newCount;
  };

  // starts a new checklistLog log
  const handleStart = async (checklistId: string) => {
    setLoading(true);
    const newLog = await xeniaApi.createChecklistLog({
      checklistId,
      hotelId: workspaceId,
      postData: {
        ...(taskId ? { taskId } : {}),
      },
    });
    setChecklistLog(newLog?.data);
    setLocalChecklistLog(newLog?.data);
    newLog?.data &&
      setChecklistProgAndScore(extractChecklistProgAndScore(newLog?.data));
    setLoading(false);
    onChecklistLoadSuccess?.();
  };

  // continue a checklistLog log
  const handleContinue = async (checklistLogId: string) => {
    const logData = await xeniaApi.getChecklistLog({
      logId: checklistLogId,
      hotelId: workspaceId,
      filtered: true,
    });
    setChecklistLog(logData?.data);
    setLocalChecklistLog(logData?.data);
    logData?.data &&
      setChecklistProgAndScore(extractChecklistProgAndScore(logData?.data));
    setLoading(false);
    onChecklistLoadSuccess?.();
  };

  const getChecklistAutomations = async (checklistId: string) => {
    const automations = await xeniaApi.getChecklistAutomations({
      workspaceId,
      checklistId,
    });
    const groupByAutomations = groupBy(
      map(automations, (automation) => ({
        id: automation.id,
        conditions: automation.conditions,
        actions: automation.actions,
        EntityId: automation.EntityId,
        entityType: automation.entityType,
      })),
      'EntityId',
    );
    setAutomations(groupByAutomations);
  };

  const getInitialErrors = (checklistLog) => {
    const errors = {};
    checklistLog?.TaskChecklistItems?.forEach(
      (cItem) => (errors[cItem.id] = []),
    );
    return errors;
  };

  const resetItemErrors = () => {
    setItemErrors(getInitialErrors(localChecklistLog));
  };

  const getFollowupQuestions = (item) => {
    if (!item?.answers?.value) return null;

    const conditionData = {
      answer: item.answers.value,
      itemType: item.type,
      automations: automations?.[item.ChecklistItemId],
    };

    const actions = validateItemAutomations(conditionData);

    const followupAction: any = actions.find(
      (action: any) => action.type === AUTOMATION_ACTIONS.FOLLOWUP_QUESTIONS,
    );

    if (!followupAction || !childItemsMap?.[item?.ChecklistItemId]?.length)
      return null;

    const childs = childItemsMap[item.ChecklistItemId];
    const childItemsGroupByAutomation = groupBy(childs, 'RelatedAutomationId');

    return childItemsGroupByAutomation[followupAction?.automationId];
  };

  const getAutomationActions = (item) => {
    const conditionData = {
      answer: item?.answers?.value,
      itemType: item?.type,
      automations: automations?.[item.ChecklistItemId],
    };
    return validateItemAutomations(conditionData);
  };

  const getAutomationErrors = (item) => {
    let actions = getAutomationActions(item);
    actions = actions.filter(
      (a) => a.type !== AUTOMATION_ACTIONS.SEND_NOTIFICATION,
    );
    const errObj: {
      hasError: boolean;
      error: string | null;
      isAutomation: boolean;
    } = {
      hasError: false,
      error: null,
      isAutomation: actions?.length > 0,
    };

    for (let i = 0; i < actions.length; i++) {
      if (errObj.hasError) break;
      const element = actions[i];
      if (element.type === AUTOMATION_ACTIONS.REQUITE_TASK) {
        if (item.Tasks.length > 0) {
          errObj['hasError'] = false;
          errObj['error'] = null;
        } else {
          errObj['hasError'] = true;
          errObj['error'] =
            'Your response requires you to create a task for completion';
        }
      }
      if (element.type === AUTOMATION_ACTIONS.REQUIRE_IMAGE) {
        if (
          item.TaskChecklistItemNotes.some(
            (note) => note.attachments.length > 0,
          )
        ) {
          errObj['hasError'] = false;
          errObj['error'] = null;
        } else {
          errObj['hasError'] = true;
          errObj['error'] = 'Your response requires you to add an image';
        }
      }
    }

    return errObj;
  };

  const getItemError = (cItem: any) => {
    const automationErrors = getAutomationErrors(cItem);
    if (automationErrors.hasError) {
      return automationErrors.error;
    }
    const imageRequired =
      cItem.isImageOrNoteRequired &&
      !cItem?.TaskChecklistItemNotes?.some(
        (note) => note?.attachments?.length > 0,
      );
    const hasAnswers = cItem.answers?.value;
    if (!hasAnswers) {
      if (imageRequired) {
        return 'This step requires an Image';
      } else if (cItem.required) {
        return 'This step is required';
      } else if (
        cItem.type === 'instruction' &&
        cItem.options.requireAcknowledgement
      ) {
        return 'This step requires acknowledgement';
      }
    } else if (imageRequired) {
      return 'This step requires an Image';
    }
    return null;
  };

  const getCurrentItemFollowUpQuestions = (item, checklistItems) => {
    const automationActions = getAutomationActions(item);
    const followUpQuestions = checklistItems?.filter(
      (fItem) =>
        fItem.ParentId === item.ChecklistItemId &&
        automationActions?.length > 0 &&
        automationActions?.some(
          (a) => a.type === AUTOMATION_ACTIONS.FOLLOWUP_QUESTIONS,
        ),
    );
    return followUpQuestions || [];
  };

  const requiredItemCount = useMemo(() => {
    const parentItems = localChecklistLog?.TaskChecklistItems?.filter(
      (item) => item.ParentId === null,
    );

    const shouldCount = (cItem, prevCount) => {
      const isStepRequired = cItem.required;
      const isInstructionRequired =
        cItem.type === 'instruction' && cItem.options.requireAcknowledgement;
      const isImageRequired =
        cItem.isImageOrNoteRequired &&
        !cItem?.TaskChecklistItemNotes?.some(
          (note) => note?.attachments?.length > 0,
        );
      const isAnswered = !!cItem?.answers?.value;
      const automationErrors = getAutomationErrors(cItem);

      if (isImageRequired) return prevCount + 1;
      if (automationErrors.hasError) return prevCount + 1;

      const shouldCount =
        (isStepRequired ||
          isInstructionRequired ||
          isImageRequired ||
          automationErrors.hasError) &&
        !isAnswered;

      return shouldCount ? prevCount + 1 : prevCount;
    };

    const parentRequiredItemCount = parentItems?.reduce((prev, curr) => {
      return shouldCount(curr, prev);
    }, 0);

    let followUpRequiredItemCount = 0;
    localChecklistLog?.TaskChecklistItems?.forEach((cItem) => {
      const followUpQuestions: any = getFollowupQuestions(cItem);
      if (followUpQuestions?.length > 0) {
        followUpQuestions.forEach((followUp) => {
          followUpRequiredItemCount += shouldCount(followUp, 0);
        });
      }
    });

    return parentRequiredItemCount + followUpRequiredItemCount;
  }, [localChecklistLog?.TaskChecklistItems]);

  const requiredItems = useMemo(() => {
    const checkRequirementCriteria = (item, skipParentCheck = false) => {
      const itemRequired = item.required;
      const itemInstructionRequired =
        item.type === 'instruction' && item.options.requireAcknowledgement;
      const itemImageRequired = item.isImageOrNoteRequired;
      const isHeaderItem = item.type === 'header';
      const isParentItem = skipParentCheck ? true : item.ParentId === null;
      const automationData = getAutomationErrors(item);
      const isRequiredAutomation =
        automationData.isAutomation && automationData.hasError;
      if (itemImageRequired && isParentItem) {
        return true;
      }
      return (
        (itemRequired ||
          itemInstructionRequired ||
          isHeaderItem ||
          isRequiredAutomation) &&
        isParentItem
      );
    };

    return localChecklistLog?.TaskChecklistItems?.filter((item) => {
      const hasRequirementCriteria = checkRequirementCriteria(item);
      const followUpQuestions = getCurrentItemFollowUpQuestions(
        item,
        localChecklistLog?.TaskChecklistItems,
      );
      const hasRequiredFollowUp = followUpQuestions?.some((fItem) =>
        checkRequirementCriteria(fItem, true),
      );
      return hasRequirementCriteria || hasRequiredFollowUp;
    });
  }, [localChecklistLog]);

  const getRequiredStepsErrors = (log) => {
    const items = log.TaskChecklistItems;
    const _errors = getInitialErrors(log);

    const appendError = (itemId: string, error: string) => {
      _errors[itemId] = [..._errors[itemId], error];
    };

    // write logic here for required checklistLog item and set errors
    // does not check for follow up item
    items.forEach((cItem) => {
      if (cItem.type !== 'header' && cItem.ParentId === null) {
        const error = getItemError(cItem);
        if (error) {
          appendError(cItem.id, error);
        }
      }
    });

    // check for follow up questions only
    items.forEach((cItem) => {
      const _followUps: any = getFollowupQuestions(cItem);
      if (cItem.type !== 'header' && _followUps?.length > 0) {
        _followUps.forEach((followUp) => {
          const error = getItemError(followUp);
          if (error) {
            appendError(followUp.id, error);
          }
        });
      }
    });

    return {
      errors: _errors,
      hasErrors: Object.values(_errors).some((e: any) => e.length > 0),
    };
  };

  const checkErrorsBeforeSubmission = () => {
    const { errors, hasErrors } = getRequiredStepsErrors(localChecklistLog);
    setItemErrors(errors);
    return !hasErrors;
  };

  const setItemError = ({ item, error }) => {
    const erroredChecklist = cloneDeep(checklistLog);
    const erroredItem = cloneDeep(item);
    erroredItem.error = error;
    erroredChecklist.TaskChecklistItems = checklistLog.TaskChecklistItems.map(
      (i) => {
        if (item.id === i.id) {
          return erroredItem;
        }
        return i;
      },
    );
    setChecklistLog(erroredChecklist);
  };

  const setSingleItemError = (item: any) => {
    const error = getItemError(item);
    setItemErrors({
      ...itemErrors,
      [item.id]: error ? [error] : [],
    });
  };

  const calculateSectionProgress = (checklistLog, sectionId) => {
    const items = checklistLog.TaskChecklistItems.filter(
      (i) => i.type !== 'header' && i.sectionId === sectionId,
    );
    return calculateProgress({
      ...checklistLog,
      TaskChecklistItems: items,
    });
  };

  const calculateSectionScore = (checklistLog, sectionId) => {
    const items = checklistLog.TaskChecklistItems.filter(
      (i) => i.type !== 'header' && i.sectionId === sectionId,
    );
    return calculateScore({
      ...checklistLog,
      TaskChecklistItems: items,
    });
  };

  const calculateScore = (checklistLog) => {
    const isItemComplete = (i) => {
      if (i.type === 'passFail') return i.answers?.value === 'pass';
      return (
        !!i.answers?.value &&
        i.answers?.value !== null &&
        i.answers?.value !== undefined
      );
    };

    const computeFollowUpScore = (item, checklistItems, answered = true) => {
      const followUpItems = getCurrentItemFollowUpQuestions(
        item,
        checklistItems,
      );
      let followUpScore = 0;
      if (followUpItems && followUpItems?.length > 0) {
        followUpScore = followUpItems.reduce((fPrev, fCurr) => {
          return (
            fPrev +
            (answered && isItemComplete(fCurr)
              ? fCurr.score
              : !answered
              ? fCurr.score
              : 0)
          );
        }, 0);
      }
      return followUpScore;
    };

    const items = checklistLog.TaskChecklistItems.filter(
      (i) => i.type !== 'header' && i.ParentId === null,
    );
    const totalScore = items?.reduce((prev, curr) => {
      const followUpScore = computeFollowUpScore(
        curr,
        checklistLog.TaskChecklistItems,
        false,
      );
      return prev + curr.score + followUpScore;
    }, 0);
    const completedSteps = items.filter((i) => isItemComplete(i));
    const earnedScore = completedSteps?.reduce((prev, curr) => {
      const followUpScore = computeFollowUpScore(
        curr,
        checklistLog.TaskChecklistItems,
      );
      return prev + curr.score + followUpScore;
    }, 0);
    return {
      percentage: Math.round(Math.ceil((earnedScore / totalScore) * 100)),
      earned: earnedScore,
      total: totalScore,
    };
  };

  const calculateProgress = (checklistLog) => {
    const isItemComplete = (i) =>
      !!i.answers?.value &&
      i.answers?.value !== null &&
      i.answers?.value !== undefined;

    const items = checklistLog.TaskChecklistItems.filter(
      (i) => i.type !== 'header' && i.ParentId === null,
    );
    const totalItemCount = items.reduce((prev, curr) => {
      const followUpItems = getFollowupQuestions(curr);
      return prev + (followUpItems?.length || 0) + 1;
    }, 0);
    const completedItemCount = items.reduce((prev, curr) => {
      const followUpItems = getCurrentItemFollowUpQuestions(
        curr,
        checklistLog?.TaskChecklistItems,
      );
      let followUpItemCount = 0;
      if (followUpItems && followUpItems?.length > 0) {
        followUpItemCount = followUpItems.reduce((fPrev, fCurr) => {
          return fPrev + (isItemComplete(fCurr) ? 1 : 0);
        }, 0);
      }
      const parentCount = isItemComplete(curr) ? 1 : 0;
      return prev + parentCount + followUpItemCount;
    }, 0);
    return {
      progress: Math.round(
        Math.ceil((completedItemCount / totalItemCount) * 100),
      ),
      completedCount: completedItemCount,
      completed: completedItemCount,
      count: totalItemCount,
      total: totalItemCount,
    };
  };

  const handleUpdateLocalChecklistItem = (item, answer) => {
    const _localChecklist = cloneDeep(localChecklistLog);
    _localChecklist.TaskChecklistItems = _localChecklist.TaskChecklistItems.map(
      (i) => {
        if (item.id === i.id) {
          return {
            ...i,
            answers: {
              value: answer,
            },
            completedAt: answer ? new Date().toISOString() : null,
            Updater: currentUser,
          };
        }
        return i;
      },
    );
    setLocalChecklistLog(_localChecklist);
    return _localChecklist;
  };

  const handleStatus = useCallback(
    async ({ item, response }) => {
      handleUpdateLocalChecklistItem(item, response?.answers?.value);
      setCurrentItemId(item.id);
      const res = await setChecklistLogItemStatus({
        logId: finalLogId,
        hotelId: checklistLog?.HotelId,
        itemId: item.id,
        data: response,
      });
      if (res?.error) {
        const error = res?.error?.message || 'Something went wrong, try again.';
        setItemError({ item, error });
      } else if (res.data) {
        if (res.data?.data?.Checklist) {
          setChecklistProgAndScore(
            extractChecklistProgAndScore(res.data.data.Checklist),
          );
          setLocalChecklistLog({
            ...localChecklistLog,
            TaskChecklistItems: res.data.data.Checklist.TaskChecklistItems,
          });
          setChecklistLog({
            ...checklistLog,
            TaskChecklistItems: res.data.data.Checklist.TaskChecklistItems,
          });
        }
      }
      return res?.data;
    },
    [checklistLog?.id, setChecklistLog, setItemError, finalLogId, logId],
  );

  // set error or refresh checklistLog after creation / update
  const handleNotePostToItemUpdateResponse = async ({
    res,
    item,
    response = null,
  }) => {
    if (res?.error) {
      const error = res?.error?.message || 'Something went wrong, try again.';
      setItemError({ item, error });
    } else if (res.data) {
      if (response) {
        const res = await handleStatus({ item, response });
      }
      handleContinue(checklistLog?.id);
    }
  };

  // create an item note in checklistLog
  const handleNote = useCallback(
    async ({ item, note = '', attachments = [], response = null }) => {
      if (!note?.trim().length && !attachments?.length) return null;
      const apiData = {
        logId: finalLogId,
        hotelId: checklistLog?.HotelId,
        itemId: item.id,
        data: {
          text: note,
          attachments,
        },
      };
      const res = await createChecklistLogItemNote(apiData);
      await handleNotePostToItemUpdateResponse({
        response,
        item,
        res,
      });
      setCurrentItemId(item.id);
      return res?.data;
    },
    [checklistLog?.id, handleStatus, setItemError, finalLogId],
  );

  // update an item note
  const handleUpdateNote = useCallback(
    async ({
      item,
      note = '',
      attachments = [],
      response = null,
      noteId = null,
    }) => {
      if (!note?.trim().length && !attachments?.length) return null;
      const apiData = {
        logId: finalLogId,
        hotelId: checklistLog?.HotelId,
        itemId: item.id,
        data: {
          text: note,
          attachments,
        },
        noteId,
      };
      const res = await updateChecklistLogItemNote(apiData);
      await handleNotePostToItemUpdateResponse({ response, item, res });
      return res?.data;
    },
    [checklistLog?.id, handleStatus, setItemError, finalLogId],
  );

  // delete single item note from checklistLog
  const handleDeleteChecklistItemNote = useCallback(
    async ({ noteId = null, item }) => {
      const res = await deleteChecklistItemNoteApi({
        noteId,
        logId: finalLogId,
        hotelId: checklistLog?.HotelId,
        itemId: item.id,
      });
      await handleNotePostToItemUpdateResponse({ item, res });
      setCurrentItemId(item.id);
      return res?.data;
    },
    [checklistLog?.id, handleStatus, setItemError, finalLogId],
  );

  // set error or refresh checklistLog after main note creation / update
  const handleNotePostToChecklistUpdateResponse = async (res) => {
    if (res?.error) {
      const error = res?.error?.message || 'Something went wrong, try again.';
      setChecklistLog({ ...checklistLog, error });
    } else if (res.data) {
      handleContinue(checklistLog?.id);
    }
  };

  // create checklistLog main note
  const handleChecklistNote = useCallback(
    async ({ note = '', attachments = [] }) => {
      if (!note?.trim().length && !attachments?.length) return null;
      const res = await createChecklistLogNote({
        logId: finalLogId,
        hotelId: checklistLog.HotelId,
        data: {
          text: note,
          attachments,
        },
      });
      await handleNotePostToChecklistUpdateResponse(res);
      return res?.data;
    },
    [checklistLog, setChecklistLog, logId],
  );

  // update checklistLog main note
  const handleUpdateChecklistNote = useCallback(
    async ({ note = '', attachments = [], noteId }) => {
      if (!note?.trim().length && !attachments?.length) return null;
      const res = await updateChecklistLogNoteApi({
        logId,
        hotelId: checklistLog?.HotelId,
        noteId,
        data: {
          text: note,
          attachments,
        },
      });
      await handleNotePostToChecklistUpdateResponse(res);
      return res?.data;
    },
    [checklistLog, setChecklistLog, logId],
  );

  // update checklistLog main note
  const handleDeleteChecklistNote = useCallback(
    async ({ noteId }) => {
      const res = await deleteChecklistLogNoteApi({
        logId,
        hotelId: checklistLog?.HotelId,
        noteId,
      });
      await handleNotePostToChecklistUpdateResponse(res);
      return res?.data;
    },
    [checklistLog, setChecklistLog, logId],
  );

  const createCorrectiveTaskCb = useCallback(
    ({
      task,
      item,
      checklistLog,
    }: {
      task: any;
      item: any;
      checklistLog?: any;
    }) => {
      const _localChecklist = cloneDeep(checklistLog ?? localChecklistLog);
      const _item = _localChecklist?.TaskChecklistItems?.find(
        (cItem) => cItem.id === item.id,
      );
      _item.Tasks = [..._item.Tasks, task];
      _localChecklist.TaskChecklistItems =
        _localChecklist.TaskChecklistItems.map((i) => {
          if (i.id === item.id) {
            return _item;
          }
          return i;
        });
      setLocalChecklistLog(_localChecklist);
      setChecklistLog(_localChecklist);
      setCurrentItemId(item.id);
    },
    [localChecklistLog, checklistLog],
  );

  useEffect(() => {
    const keyedChildItems = groupBy(
      filter(localChecklistLog?.TaskChecklistItems, (item) => item?.ParentId),
      'ParentId',
    );
    setChildItemsMap(keyedChildItems || null);
  }, [checklistLog, setChecklistLog, setChildItemsMap, localChecklistLog]);

  return {
    loading,
    localChecklist: localChecklistLog,
    checklist: checklistLog,
    childItemsMap,
    automations,
    itemErrors,
    requiredItems,
    requiredItemCount,
    checklistProgAndScore,
    resetItemErrors,
    checkErrorsBeforeSubmission,
    handleStatus,
    handleNote,
    handleUpdateNote,
    handleDeleteChecklistItemNote,
    handleChecklistNote,
    handleUpdateChecklistNote,
    handleDeleteChecklistNote,
    createCorrectiveTaskCb,
    setSingleItemError,
  };
};

export default useChecklistLog;
